import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import {
  acceptMaterialStockTransfersApi,
  AvailableMaterialsQuery,
  cancelMaterialStockTransfersApi,
  createMaterialStockTransferApi,
  fetchAvailableTransferMaterialsApi,
  fetchPendingIncomingMaterialTransfersApi,
  fetchPendingOutgoingMaterialTransfersApi,
} from "../../../api/StockTransfer/materialStockTransfer.api";
import {
  AvailableTransferMaterial,
  CreateMaterialTransferPayload,
  MaterialTransferPage,
} from "../../../models/materialStockTransfer.model";

interface TransferListState extends MaterialTransferPage {
  loading: boolean;
}

interface MaterialStockTransferState {
  incoming: TransferListState;
  outgoing: TransferListState;
  actionLoading: boolean;
  materialsLoading: boolean;
  error: string | null;
}

const emptyList = (): TransferListState => ({
  transfers: [],
  total: 0,
  page: 1,
  totalPages: 1,
  loading: false,
});

const initialState: MaterialStockTransferState = {
  incoming: emptyList(),
  outgoing: emptyList(),
  actionLoading: false,
  materialsLoading: false,
  error: null,
};

const getErrorMessage = (error: unknown, fallback: string) => {
  if (axios.isAxiosError<{ message?: string }>(error)) {
    return error.response?.data?.message || fallback;
  }
  return error instanceof Error ? error.message : fallback;
};

export const fetchAvailableTransferMaterials = createAsyncThunk<
  {
    materials: AvailableTransferMaterial[];
    pagination?: { totalPages?: number };
  },
  AvailableMaterialsQuery,
  { rejectValue: string }
>(
  "materialStockTransfer/fetchAvailableMaterials",
  async (query, { rejectWithValue }) => {
    try {
      return await fetchAvailableTransferMaterialsApi(query);
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to fetch available materials"),
      );
    }
  },
);

export const fetchPendingIncomingMaterialTransfers = createAsyncThunk<
  MaterialTransferPage,
  number | undefined,
  { rejectValue: string }
>(
  "materialStockTransfer/fetchIncoming",
  async (page = 1, { rejectWithValue }) => {
    try {
      return await fetchPendingIncomingMaterialTransfersApi(page);
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to load incoming material transfers"),
      );
    }
  },
);

export const fetchPendingOutgoingMaterialTransfers = createAsyncThunk<
  MaterialTransferPage,
  number | undefined,
  { rejectValue: string }
>(
  "materialStockTransfer/fetchOutgoing",
  async (page = 1, { rejectWithValue }) => {
    try {
      return await fetchPendingOutgoingMaterialTransfersApi(page);
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to load outgoing material transfers"),
      );
    }
  },
);

export const createMaterialTransfer = createAsyncThunk<
  unknown,
  CreateMaterialTransferPayload,
  { rejectValue: string }
>(
  "materialStockTransfer/create",
  async (payload, { rejectWithValue }) => {
    try {
      return await createMaterialStockTransferApi(payload);
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to create material transfers"),
      );
    }
  },
);

export const acceptMaterialTransfers = createAsyncThunk<
  Array<{ transferId: string; remark?: string }>,
  Array<{ transferId: string; remark?: string }>,
  { rejectValue: string }
>(
  "materialStockTransfer/accept",
  async (transfers, { rejectWithValue }) => {
    try {
      await acceptMaterialStockTransfersApi(transfers);
      return transfers;
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to accept material transfers"),
      );
    }
  },
);

export const cancelMaterialTransfers = createAsyncThunk<
  Array<{ transferId: string; remark?: string }>,
  Array<{ transferId: string; remark?: string }>,
  { rejectValue: string }
>(
  "materialStockTransfer/cancel",
  async (transfers, { rejectWithValue }) => {
    try {
      await cancelMaterialStockTransfersApi(transfers);
      return transfers;
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to cancel material transfers"),
      );
    }
  },
);

const materialStockTransferSlice = createSlice({
  name: "materialStockTransfer",
  initialState,
  reducers: {
    clearMaterialTransferError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAvailableTransferMaterials.pending, (state) => {
        state.materialsLoading = true;
      })
      .addCase(fetchAvailableTransferMaterials.fulfilled, (state) => {
        state.materialsLoading = false;
      })
      .addCase(fetchAvailableTransferMaterials.rejected, (state, action) => {
        state.materialsLoading = false;
        state.error = action.payload || "Failed to fetch available materials";
      })
      .addCase(fetchPendingIncomingMaterialTransfers.pending, (state) => {
        state.incoming.loading = true;
        state.error = null;
      })
      .addCase(
        fetchPendingIncomingMaterialTransfers.fulfilled,
        (state, action: PayloadAction<MaterialTransferPage>) => {
          state.incoming = { ...action.payload, loading: false };
        },
      )
      .addCase(
        fetchPendingIncomingMaterialTransfers.rejected,
        (state, action) => {
          state.incoming.loading = false;
          state.error =
            action.payload || "Failed to load incoming material transfers";
        },
      )
      .addCase(fetchPendingOutgoingMaterialTransfers.pending, (state) => {
        state.outgoing.loading = true;
        state.error = null;
      })
      .addCase(
        fetchPendingOutgoingMaterialTransfers.fulfilled,
        (state, action: PayloadAction<MaterialTransferPage>) => {
          state.outgoing = { ...action.payload, loading: false };
        },
      )
      .addCase(
        fetchPendingOutgoingMaterialTransfers.rejected,
        (state, action) => {
          state.outgoing.loading = false;
          state.error =
            action.payload || "Failed to load outgoing material transfers";
        },
      )
      .addCase(createMaterialTransfer.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(createMaterialTransfer.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(createMaterialTransfer.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Failed to create material transfers";
      })
      .addCase(acceptMaterialTransfers.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(acceptMaterialTransfers.fulfilled, (state, action) => {
        state.actionLoading = false;
        const acceptedIds = new Set(
          action.payload.map((transfer) => transfer.transferId),
        );
        state.incoming.transfers = state.incoming.transfers.filter(
          (transfer) => !acceptedIds.has(transfer._id),
        );
        state.incoming.total = Math.max(
          0,
          state.incoming.total - acceptedIds.size,
        );
      })
      .addCase(acceptMaterialTransfers.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Failed to accept material transfers";
      })
      .addCase(cancelMaterialTransfers.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(cancelMaterialTransfers.fulfilled, (state, action) => {
        state.actionLoading = false;
        const cancelledIds = new Set(
          action.payload.map((transfer) => transfer.transferId),
        );
        state.outgoing.transfers = state.outgoing.transfers.filter(
          (transfer) => !cancelledIds.has(transfer._id),
        );
        state.outgoing.total = Math.max(
          0,
          state.outgoing.total - cancelledIds.size,
        );
      })
      .addCase(cancelMaterialTransfers.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Failed to cancel material transfers";
      });
  },
});

export const { clearMaterialTransferError } =
  materialStockTransferSlice.actions;
export default materialStockTransferSlice.reducer;
